/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../../../registry/Component.type';
import { ItemType } from '../../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../../registry/Components';
import { isEmpty } from '../../../../utils/isEmpty';
import { baseColumnProps, isBaseColumnPropertyVisible } from '../BaseColumn';
import { defaultValue } from './SwitchColumn';

const SwitchColumnManager: ComponentManagerFn<'SwitchColumn'> = (
  platform: Platform,
): ComponentManager<'SwitchColumn'> => {
  return {
    ...baseComponentManager<'SwitchColumn'>(platform, 'SwitchColumn'),
    type: 'SwitchColumn',
    name: 'Switch Column',
    isChildAllowed: (page, item, childType) =>
      (['SwitchColumnCase'] as ItemType[]).includes(childType),
    defaultValue,
    defaultChildren: ['SwitchColumnCase', 'SwitchColumnCase'],
    props: {
      ...baseColumnProps,
      expression: {
        label: 'Switch Expression',
        type: 'expression',
        returnType: async () => 'string',
        helpText:
          'Must return a string value that will be used to find a matching Switch Column Case',
      },
      viewAttribute: {
        ...baseColumnProps.viewAttribute,
        required: false,
        helpText:
          'If set, the value of this attribute will be used for sorting the Switch Column',
      },
    },
    isPropertyVisible(prop, props, { appUid, itemId, pageId }): boolean {
      if (
        itemId &&
        !isBaseColumnPropertyVisible({
          appUid,
          platform,
          itemId,
          pageId,
          prop,
        })
      ) {
        return false;
      }
      switch (prop) {
        case 'filterable':
          return false;
        case 'sortable':
          return !isEmpty(props.viewAttribute);
        case 'toolTip':
        case 'toolTipPosition':
        case 'toolTipMaxWidth':
          return false;
        default:
          return true;
      }
    },
    getDisplayText: (item) => {
      const label = item.props.label.value ?? '';
      const exp = item.props.expression.value ?? '';
      return `${label} ${exp.trim().length > 0 ? `(${exp})` : ''}`;
    },
    category: 'Data Grid',
    icon: { iconName: 'ballot-check' },
    description: `**Overview**<br/>
The Switch Column is a polymorphic Data Grid column that allows you to render different types of components in the same column based on the data in each row.
Standard columns force every row to look the same (e.g., every cell is a Text Field). The Switch Column breaks this rule. It acts as a conditional logic container (like a programming switch statement), letting you decide dynamically whether a specific cell should display a String Input, a Checkbox, a Date Picker, or a Button.

**1. The Logic (The Switch Expression)**<br/>
The core of this component is a single expression that runs for every row to decide "What type of data is this?"
* **Switch Expression:** A string expression that evaluates the current row's data.
* **Return Value:** It must return a string key (e.g., "TEXT", "BOOL", "DATE") that matches one of the Switch Column Cases you define.
* **Example:** \`$.settingType\` (where the settingType field in the database contains values like 'Text', 'Boolean', etc.).

**2. Structure (The Cases)**<br/>
The Switch Column is a container. To make it work, you must add Switch Column Case components as children.
* **Child Component (Switch Column Case):** Each child represents one "Branch" of logic.
* **Configuration:**
    * **Case:** The string value to match (e.g., "BOOL").
    * **Child:** The actual column component to render (e.g., a Checkbox Column).
    * **Default Case:** If you add a Case component and leave its Case property empty, it acts as the fallback. It renders if the expression returns a value that matches no other case.

**3. Sorting Behavior**<br/>
Since this column displays different data types (Numbers in one row, Text in another), automatic sorting is impossible without help.
* **View Attribute (Optional):**
    * **Usage:** Bind this to a specific attribute in the store (e.g., "sortValue") to define how this column should be sorted.
    * **Effect:** If set, the **Sortable** property becomes available. If not set, sorting is disabled for this column.

**4. Best Use Cases**<br/>
* **Configuration Grids:** A "System Settings" table where:
    * Row 1 (Server Name) shows a String Column.
    * Row 2 (Max Connections) shows a Number Column.
    * Row 3 (Enable Logging) shows a Checkbox Column.
* **Questionnaires:** A survey grid where the answer input changes type based on the question type.

**5. Limitations**<br/>
* **No Filtering:** Because the data types vary per row, the standard grid filter header is disabled for this column.`,
  };
};

export default SwitchColumnManager;
